import { Attribute, AttributeTwo } from "../types/attribute";
import { LocaleText } from "../types/locale";
import { Material, MaterialFormData, MaterialType, MaterialTypeTwo, MaterialsList } from "../types/material";
import {
  AttachProductFormData,
  ChildProductFormData,
  CustomProductOption,
  CustomProductSuggestion,
  LoadProductParams,
  Product,
  ProductFormData,
  ProductImport,
  ProductImportRst,
  ProductSearchRst,
  TopUpSuggestion,
} from "../types/product";
import { get, post } from "./fetch.core";
import { initPromise } from "./init.core";

// Attributes
async function listAttributes() {
  const res = await get<Attribute[]>("/admin/material/attribute/list");
  return res.data;
}

async function getListAttributes() {
  const res = await get<AttributeTwo[]>("/admin/material/attribute/getList");
  return res.data;
}

async function getAttribute(id: string) {
  const res = await get<Attribute>(`/admin/material/attribute/get?id=${id}`);
  return res.data;
}

const attributes = {
  list: listAttributes,
  get: getAttribute,
  getList: getListAttributes,
};

async function listMaterialTypes() {
  const res = await get<MaterialType[]>("/admin/material-type/list");
  return res.data;
}

async function getListMaterialTypes() {
  const res = await get<MaterialTypeTwo[]>("/admin/material-type/getList");
  return res.data;
}

async function getMaterialType(id: string) {
  const res = await get<MaterialType>(`/admin/material-type/get?id=${id}`);
  return res.data;
}

const materialType = {
  list: listMaterialTypes,
  get: getMaterialType,
  getList: getListMaterialTypes,
};

export async function loadMaterials(params: Partial<LoadProductParams>) {
  if (!params.search) {
    delete params.search;
  }

  if (!params.categoryIds || params.categoryIds.length === 0) {
    delete params.categoryIds;
  }

  return (await post<MaterialsList>("/admin/material/list", params)).data;
}

export async function getMaterial(materialId: string) {
  await initPromise;
  return (await get<Material>(`/admin/material/get?materialId=${materialId}`)).data;
}

export async function getCustomOptions(productId: string) {
  return (await get<CustomProductOption[]>(`/admin/product/getCustomOptions?productId=${productId}`)).data;
}

export async function updateCustomOptions(data: { productId: string; customOptions: CustomProductOption[] }) {
  return (await post<CustomProductOption[]>(`/admin/product/updateCustomOptions`, data)).data;
}

export async function getCustomSuggestions(productId: string) {
  return (await get<CustomProductSuggestion[]>(`/admin/product/getCustomSuggestions?productId=${productId}`)).data;
}

export async function updateCustomSuggestions(data: { productId: string; customSuggestions: CustomProductSuggestion[] }) {
  return (await post<CustomProductSuggestion[]>(`/admin/product/updateCustomSuggestions`, data)).data;
}

export async function getTopUpSuggestions(productId: string) {
  return (await get<TopUpSuggestion[]>(`/admin/product/getTopUpSuggestions?productId=${productId}`)).data;
}

export async function updateTopUpSuggestions(data: { productId: string; topUpSuggestions: TopUpSuggestion[] }) {
  return (await post<TopUpSuggestion[]>(`/admin/product/updateTopUpSuggestions`, data)).data;
}

function sanitizeFormData(data: MaterialFormData) {
  return data;
}

export async function createMaterial(data: MaterialFormData) {
  await post("/admin/material/create", sanitizeFormData(data));
}

export async function updateMaterial(materialId: string, data: MaterialFormData) {
  const updateData = { ...sanitizeFormData(data), materialId };
  await post("/admin/material/update", updateData);
}

export async function importProducts(productImports: ProductImport[]) {
  const rst = await post<ProductImportRst[]>("/admin/product/import", { products: productImports });
  return rst.data;
}

async function createChildProduct(data: ChildProductFormData, parentProduct: Product) {
  const d = sanitizeChildFormData(data, parentProduct);
  await post("/admin/product/create", d);
}

async function updateChildProduct(data: ChildProductFormData, parentProduct: Product, childProductId: string) {
  const updateData = { ...sanitizeChildFormData(data, parentProduct), productId: childProductId };
  await post("/admin/product/update", updateData);
}

async function deleteMaterial(materialId: string) {
  const res = await post<{ message: string }>("/admin/material/delete", { materialId });
  return res.data;
}

export async function attachProduct(data: AttachProductFormData) {
  await post("/admin/product/attachProduct", data);
}

function sanitizeChildFormData(data: ChildProductFormData, parentProduct: Product) {
  // if (localeCore.isEmpty(data.name)) delete data.name;
  // if (localeCore.isEmpty(data.description)) delete data.description;

  return {
    productType: "variationChild",
    parentId: parentProduct.id,
    price: data.price,
    name: data.name,
    galleryIds: data.galleryIds,
    categoryIds: data.categoryIds,
    featuredImgId: data.featuredImgId,
    descriptionGalleryIds: data.descriptionGalleryIds,
    sku: data.sku,
    availability: data.availability,
    barcode: data.barcode,
    // stockReadyDate: data.stockReadyDate === "" ? undefined : data.stockReadyDate,
    attributes: data.attributes.map((a) => {
      return {
        attributeId: a.attributeId,
        value: a.value,
      };
    }),
  };
}

async function searchProduct(search: string) {
  const res = await post<ProductSearchRst>("/public/product/list", {
    search,
  });
  return res.data;
}

const materialCore = {
  createChildProduct,
  updateChildProduct,
  searchProduct,
  deleteMaterial,
  attachProduct,
  attributes,
  materialType,
};

export default materialCore;
